/**
 * Leaderboard Component
 * Display game rankings
 */

import React from "react";
import { View, Text, FlatList, TouchableOpacity, StyleSheet, Image } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { LeaderboardEntry } from "../types";

interface LeaderboardProps {
  /** Leaderboard entries */
  entries: LeaderboardEntry[];
  /** Title for the leaderboard */
  title?: string;
  /** Maximum entries to show */
  maxEntries?: number;
  /** Show "See all" button */
  showSeeAll?: boolean;
  /** Callback when "See all" is pressed */
  onSeeAll?: () => void;
  /** Callback when a user entry is pressed */
  onUserPress?: (userId: string) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
}

/**
 * Rank badge component
 */
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const colors = useThemeColors();

  if (rank === 1) {
    return (
      <View style={[styles.rankBadge, { backgroundColor: "#FFD700" }]}>
        <Text style={styles.rankText}>🥇</Text>
      </View>
    );
  }
  if (rank === 2) {
    return (
      <View style={[styles.rankBadge, { backgroundColor: "#C0C0C0" }]}>
        <Text style={styles.rankText}>🥈</Text>
      </View>
    );
  }
  if (rank === 3) {
    return (
      <View style={[styles.rankBadge, { backgroundColor: "#CD7F32" }]}>
        <Text style={styles.rankText}>🥉</Text>
      </View>
    );
  }

  return (
    <View style={[styles.rankBadge, { backgroundColor: colors.surfaceSecondary }]}>
      <Text style={[styles.rankNumber, { color: colors.textSecondary }]}>
        {rank}
      </Text>
    </View>
  );
};

/**
 * Leaderboard entry row
 */
const LeaderboardRow: React.FC<{
  entry: LeaderboardEntry;
  isCurrentUser: boolean;
  onPress?: () => void;
}> = ({ entry, isCurrentUser, onPress }) => {
  const colors = useThemeColors();

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={!onPress}
      activeOpacity={0.7}
      style={[
        styles.row,
        {
          backgroundColor: isCurrentUser ? `${colors.primary}15` : colors.surface,
          borderWidth: isCurrentUser ? 1 : 0,
          borderColor: isCurrentUser ? colors.primary : "transparent",
        },
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${entry.full_name}, rang ${entry.rank}, ${entry.total_points} punten`}
    >
      <RankBadge rank={entry.rank} />

      {/* Avatar */}
      <View style={[styles.avatar, { backgroundColor: colors.primaryLight }]}>
        {entry.avatar_url ? (
          <Image source={{ uri: entry.avatar_url }} style={styles.avatarImage} />
        ) : (
          <Text style={[styles.avatarText, { color: colors.primary }]}>
            {(entry.full_name || "?").charAt(0).toUpperCase()}
          </Text>
        )}
      </View>

      {/* User info */}
      <View style={styles.userInfo}>
        <View style={styles.nameRow}>
          <Text
            style={[
              styles.name,
              { color: colors.text },
              isCurrentUser && { fontWeight: "700" },
            ]}
            numberOfLines={1}
          >
            {entry.full_name}
          </Text>
          {isCurrentUser && (
            <View style={[styles.youBadge, { backgroundColor: colors.primary }]}>
              <Text style={styles.youBadgeText}>Jij</Text>
            </View>
          )}
        </View>
        <View style={styles.statsRow}>
          <View style={styles.wvgContainer}>
            <Text style={[styles.wvgLabel, { color: "#22C55E" }]}>
              W: {entry.games_won}
            </Text>
            <Text style={[styles.wvgLabel, { color: "#EF4444" }]}>
              V: {entry.games_lost ?? 0}
            </Text>
            <Text style={[styles.wvgLabel, { color: "#F59E0B" }]}>
              G: {entry.games_drawn ?? 0}
            </Text>
          </View>
          {entry.current_streak > 0 && (
            <View style={styles.streakBadge}>
              <Ionicons name="flame" size={10} color="#EF4444" />
              <Text style={[styles.streakText, { color: "#EF4444" }]}>
                {entry.current_streak}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Wins Count */}
      <View style={styles.pointsContainer}>
        <Text style={[styles.points, { color: "#22C55E" }]}>
          {entry.games_won}
        </Text>
        <Text style={[styles.pointsLabel, { color: colors.textSecondary }]}>
          wins
        </Text>
      </View>
    </TouchableOpacity>
  );
};

/**
 * Leaderboard component
 */
export const Leaderboard: React.FC<LeaderboardProps> = ({
  entries,
  title = "Ranglijst",
  maxEntries = 10,
  showSeeAll = false,
  onSeeAll,
  onUserPress,
  isLoading = false,
  emptyMessage = "Nog geen scores",
}) => {
  const colors = useThemeColors();
  const { activeProfile } = useAuth();

  const displayEntries = maxEntries ? entries.slice(0, maxEntries) : entries;

  // Find current user if not in top entries
  const currentUserEntry = entries.find((e) => e.user_id === activeProfile?.id);
  const isCurrentUserInTop =
    currentUserEntry && displayEntries.some((e) => e.user_id === activeProfile?.id);

  if (entries.length === 0 && !isLoading) {
    return (
      <View style={styles.container}>
        {title && (
          <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
        )}
        <View
          style={[
            styles.emptyContainer,
            { backgroundColor: colors.surfaceSecondary },
          ]}
        >
          <Ionicons
            name="trophy-outline"
            size={32}
            color={colors.textTertiary}
          />
          <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
            {emptyMessage}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
        {showSeeAll && entries.length > maxEntries && (
          <TouchableOpacity onPress={onSeeAll}>
            <Text style={[styles.seeAll, { color: colors.primary }]}>
              Bekijk alles
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Entries */}
      <View style={styles.entriesContainer}>
        {displayEntries.map((entry, index) => (
          <LeaderboardRow
            key={`${entry.user_id}-${index}`}
            entry={entry}
            isCurrentUser={entry.user_id === activeProfile?.id}
            onPress={onUserPress ? () => onUserPress(entry.user_id) : undefined}
          />
        ))}

        {/* Current user if not in top */}
        {currentUserEntry && !isCurrentUserInTop && (
          <>
            <View
              style={[styles.separator, { backgroundColor: colors.border }]}
            >
              <Text style={[styles.separatorText, { color: colors.textTertiary }]}>
                • • •
              </Text>
            </View>
            <LeaderboardRow
              entry={currentUserEntry}
              isCurrentUser={true}
              onPress={
                onUserPress
                  ? () => onUserPress(currentUserEntry.user_id)
                  : undefined
              }
            />
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {},
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
  },
  seeAll: {
    fontSize: 14,
    fontWeight: "500",
  },
  entriesContainer: {
    gap: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 12,
  },
  rankBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  rankText: {
    fontSize: 16,
  },
  rankNumber: {
    fontSize: 14,
    fontWeight: "600",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    overflow: "hidden",
  },
  avatarImage: {
    width: "100%",
    height: "100%",
  },
  avatarText: {
    fontSize: 16,
    fontWeight: "700",
  },
  userInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  name: {
    fontSize: 15,
    fontWeight: "500",
    flexShrink: 1,
  },
  youBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  youBadgeText: {
    fontSize: 10,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 2,
    gap: 8,
  },
  wvgContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  wvgLabel: {
    fontSize: 11,
    fontWeight: "600",
  },
  stat: {
    fontSize: 12,
  },
  streakBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  streakText: {
    fontSize: 11,
    fontWeight: "600",
  },
  pointsContainer: {
    alignItems: "flex-end",
  },
  points: {
    fontSize: 18,
    fontWeight: "700",
  },
  pointsLabel: {
    fontSize: 11,
  },
  emptyContainer: {
    padding: 32,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
  },
  separator: {
    alignItems: "center",
    paddingVertical: 8,
  },
  separatorText: {
    fontSize: 12,
  },
});

export default Leaderboard;

